// Loading-motion primitives — see beui.dev/components/agents/loading-states for reference.
// Core principle: never fabricate progress; only present the minimal signal the system actually knows.
//   - ReasoningText: agent planning phase (all we know is "it is working") → rotating phrases + SVG cursor loader + shimmer.
//   - GenStatus (agent-progress style): image jobs (we know queued/running + elapsed) → glyph + verb + timer.
// No spinning spinner icons, no invented percentages.

import { useEffect, useState } from 'react';

/** Terminal-style loader: blinking SVG cursor (icons are always SVG, never punctuation/emoji) */
export function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

export function AsciiLoader() {
  return (
    <svg className="ascii-loader" width="9" height="14" viewBox="0 0 9 14" aria-hidden="true">
      <rect x="1" y="1" width="7" height="12" rx="1" fill="currentColor" />
    </svg>
  );
}

export interface ReasoningTextProps {
  phrases: string[];
  /** How long each phrase stays (beui default 1800ms) */
  intervalMs?: number;
}

/**
 * ReasoningText (swap variant of the same-named beui primitive):
 * rotating phrases + continuous shimmer (2.2s loop) + ASCII loader prefix.
 * key=idx forces a remount so each phrase's enter animation replays.
 */
export function ReasoningText({ phrases, intervalMs = 1800 }: ReasoningTextProps) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(v => (v + 1) % phrases.length), intervalMs);
    return () => clearInterval(t);
  }, [phrases.length, intervalMs]);
  return (
    <span className="reasoning-text" role="status" aria-live="polite">
      <AsciiLoader />
      <span key={idx} className="reasoning-phrase shimmer-text">{phrases[idx]}</span>
    </span>
  );
}

/** mm:ss (tabular-nums ensured by CSS) */
export function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}
